"use client";

import { useState, useCallback, useEffect, useRef } from "react";

type FloatingStatCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  className?: string;
};

export function FloatingStatCard({ icon, label, value, className = "" }: FloatingStatCardProps) {
  const [wiggling, setWiggling] = useState(false);
  const [pulsing, setPulsing] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setWiggling(true);
    setPulsing(true);
  }, []);

  return (
    <div
      className={`w-48 p-4 rounded-xl glass-panel shadow-xl transition-all duration-300 cursor-default ${
        wiggling ? "animate-wiggle shadow-primary/20 border-primary/20" : ""
      } ${className}`}
      onMouseEnter={handleMouseEnter}
      onAnimationEnd={() => setWiggling(false)}
      style={{ willChange: "transform" }}
    >
      <div
        className={`text-primary mb-2 transition-all duration-300 ${pulsing ? "animate-icon-pulse" : ""}`}
        onAnimationEnd={(e) => { e.stopPropagation(); setPulsing(false); }}
      >
        {icon}
      </div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

/** Simulates a live run with correlated metrics that update every second. */
export function useSimulatedRun() {
  const [stats, setStats] = useState({
    distance: "5.42 km",
    pace: "4:30 /km",
    hr: "164 bpm",
    power: "288 W",
  });

  const distRef = useRef(0);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    const startTime = performance.now();
    lastTimeRef.current = startTime;
    distRef.current = 3.2 + Math.random() * 5.9; // 3.2–9.1 km

    const massKg = 79.4; // 175 lbs

    const tick = () => {
      const now = performance.now();
      const dt = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;
      const elapsed = (now - startTime) / 1000;

      // Pace oscillates with layered sine waves for natural feel
      // Base: 270 s/km (4:30/km), range roughly ±15s (4:15–4:45)
      const paceOffset =
        Math.sin(elapsed * 0.4) * 8 +
        Math.sin(elapsed * 0.17) * 5 +
        Math.sin(elapsed * 0.07) * 2;
      const paceSeconds = 270 + paceOffset;

      // Distance accumulates at current pace (~0.0037 km/s at 4:30/km)
      distRef.current += dt / paceSeconds;

      // HR inversely correlated: faster pace (negative offset) → higher HR
      // Range: ~154–174 bpm
      const hr = Math.round(164 - paceOffset * 0.7);

      // Running power for 175lb person: mass × speed(m/s) × 0.98
      const speedMs = 1000 / paceSeconds;
      const power = Math.round(massKg * speedMs * 0.98);

      // Format pace as m:ss
      const totalSec = Math.round(paceSeconds);
      const paceMin = Math.floor(totalSec / 60);
      const paceSec = totalSec % 60;

      setStats({
        distance: `${distRef.current.toFixed(2)} km`,
        pace: `${paceMin}:${String(paceSec).padStart(2, "0")} /km`,
        hr: `${hr} bpm`,
        power: `${power} W`,
      });
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  return stats;
}
